import mapboxgl from "mapbox-gl";

const getStatusColor = ["get", "statusColor"];
const getPointCount = ["get", "point_count"];

const COLORS = {
  green: "#00a65a",
  red: "#dd4b39",
  yellow: "#f39c12",
};
const CLUSTER_STROKE_WIDTH = 6;
const CLUSTER_OPACITY = 0.9;
const CLUSTER_RADIUS_STEPS = ["step", getPointCount, 15, 5, 18, 10, 20];
const MAX_CLUSTER_ZOOM = 14;

export function addSources(map) {
  /* Add sources to the map
   * - Devices: GeoJSON source for device markers with clustering enabled
   * - Spider lines: GeoJSON source for lines connecting original and jittered coordinates
   * @param {mapboxgl.Map} map - The Mapbox map instance
   */

  map.addSource("devices", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
    cluster: true,
    clusterMaxZoom: MAX_CLUSTER_ZOOM,
    clusterRadius: 50,
    clusterProperties: {
      online_count: ["+", ["case", ["==", getStatusColor, "green"], 1, 0]],
      offline_count: ["+", ["case", ["==", getStatusColor, "red"], 1, 0]],
    },
  });

  map.addSource("spider-lines", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });
}

export function addLayers(map) {
  /* Add layers to the map
   * - Spider lines: lines connecting original and jittered coordinates
   * - Clusters: circles representing clusters of devices
   * - Cluster count: text showing the number of devices in each cluster
   * - Unclustered points: circles for individual devices
   * @param {mapboxgl.Map} map - The Mapbox map instance
   */

  map.addLayer({
    id: "spider-lines-layer",
    type: "line",
    source: "spider-lines",
    paint: {
      "line-color": "#999999",
      "line-width": 1,
      "line-dasharray": [2, 2],
    },
  });

  map.addLayer({
    id: "clusters",
    type: "circle",
    source: "devices",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": "#FFFFFF",
      "circle-opacity": CLUSTER_OPACITY,
      "circle-pitch-alignment": "map",
      "circle-radius": CLUSTER_RADIUS_STEPS,
      "circle-stroke-color": [
        "case",
        // All online
        ["==", ["get", "offline_count"], 0],
        COLORS.green,
        // All offline
        ["==", ["get", "online_count"], 0],
        COLORS.red,
        // Mixed
        COLORS.yellow,
      ],
      "circle-stroke-width": CLUSTER_STROKE_WIDTH,
      "circle-stroke-opacity": CLUSTER_OPACITY,
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: "devices",
    filter: ["has", "point_count"],
    layout: {
      "text-field": [
        "format",
        // First line: point count
        getPointCount,
        {
          "font-scale": [
            "step",
            getPointCount,
            0.9, // count < 5
            5,
            1, // count < 10
            10,
            1.2,
          ],
        },
      ],
      "text-size": 12,
      "text-anchor": "center",
      "text-justify": "center",
    },
    paint: {
      "text-color": "#000000",
    },
  });

  map.addLayer({
    id: "unclustered-point",
    type: "circle",
    source: "devices",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-radius": 6,
      "circle-color": [
        "match",
        getStatusColor,
        "green",
        COLORS.green,
        "red",
        COLORS.red,
        COLORS.yellow,
      ],
      "circle-stroke-color": "#000000",
      "circle-stroke-width": 1,
    },
  });
}

function addLayerEvent(map, layer, event, handler) {
  map.on(event, layer, handler);
  return () => map.off(event, layer, handler);
}

export function addMapEventListeners(map) {
  const onClusterClick = (e) => {
    /* Handle click on cluster
     * e.features[0] contains the clicked feature
     * We can extract cluster_id to get more information about the cluster
     * If zoom level is greater than 10, we can expand the cluster to show its leaves
     */
    const features = map.queryRenderedFeatures(e.point, {
      layers: ["clusters"],
    });
    if (!features.length || !features[0].properties.cluster_id) return;
    const clusterId = features[0].properties.cluster_id;
    const source = map.getSource("devices");

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;

      if (zoom > 10) {
        source.getClusterLeaves(clusterId, 100, 0, (err, leaves) => {
          if (err) return;

          const coordinates = leaves.map((leaf) => leaf.geometry.coordinates);

          const bounds = coordinates.reduce(
            (b, coord) => {
              return b.extend(coord);
            },
            new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
          );

          map.fitBounds(bounds, { duration: 1000, maxZoom: 18, padding: 20 });
        });
        return;
      }

      // Default behavior
      map.easeTo({
        center: features[0].geometry.coordinates,
        padding: 20,
        zoom,
      });
    });
  };

  const onUnclusteredPointClick = (e) => {
    /* Handle click on unclustered point
     * e.features[0] contains the clicked feature
     * We can extract coordinates and properties to show in a popup
     */
    const feature = e.features[0];
    const coords = feature.geometry.coordinates.slice();
    const props = feature.properties;
    const popupHtml = `
      <div class="info-window">
        <div class="header">
          <dl>
            <dt>Name / Network</dt>
            <dd>${props.name} / ${props.networkName}</dd>
            <dt>Id</dt>
            <dd>${props.id}</dd>
            <dt>Status</dt>
            <dd class="text-${props.statusColor}">${props.statusLabel}</dd>
          </dl>
        </div>
        <a href="/devices/${props.id}/overview" class="btn btn-primary w-100">View Device</a>
      </div>
    `;

    new mapboxgl.Popup()
      .setLngLat(coords)
      .setHTML(popupHtml)
      .setMaxWidth("300px")
      .addTo(map);

    map.easeTo({
      center: coords,
      offset: [0, -100],
      duration: 500,
    });
  };

  const setCursor = (cursor) => {
    map.getCanvas().style.cursor = cursor;
  };

  const removeClusterClick = addLayerEvent(
    map,
    "clusters",
    "click",
    onClusterClick,
  );
  const removePointClick = addLayerEvent(
    map,
    "unclustered-point",
    "click",
    onUnclusteredPointClick,
  );

  map.on("mouseenter", "clusters", () => setCursor("pointer"));
  map.on("mouseleave", "clusters", () => setCursor(""));

  return () => {
    removeClusterClick();
    removePointClick();
    map.off("mouseenter", "clusters", () => setCursor("pointer"));
    map.off("mouseleave", "clusters", () => setCursor(""));
  };
}
